// Register.jsx
import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { REGISTER_USER, GOOGLE_SIGN_IN, SEND_VERIFICATION_EMAIL } from '../graphql/mutations';
import AuthLayout from '../components/AuthLayout';
import debounce from 'lodash/debounce';
import { setToken } from '../utils/auth';
import { useLazyQuery } from '@apollo/client';
import { CHECK_CATALOG_NAME_AVAILABILITY, CHECK_EMAIL_AVAILABILITY } from '../graphql/queries';
import { useGoogleLogin } from '@react-oauth/google';
// import jwt_decode from "jwt-decode";
import { jwtDecode } from "jwt-decode";
import { useAuth } from '../utils/authContext';
import { validateSubdomainClient, generateSuggestions } from '../utils/reservedSubdomains';

const Register = () => {
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [catalogName, setCatalogName] = useState('');
    const [businessName, setBusinessName] = useState('');
    const [catalogNameError, setCatalogNameError] = useState('');
    const [isCatalogNameAvailable, setIsCatalogNameAvailable] = useState(null);
    const [suggestedCatalogNames, setSuggestedCatalogNames] = useState([]);
    const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);


    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const [isSubscribed, setIsSubscribed] = useState(true);

    const { login } = useAuth();
    const [registerUser, { loading, error }] = useMutation(REGISTER_USER);
    const [sendVerificationEmail] = useMutation(SEND_VERIFICATION_EMAIL);

    const [checkCatalogNameAvailability] = useLazyQuery(CHECK_CATALOG_NAME_AVAILABILITY);
    const [checkEmailAvailability] = useLazyQuery(CHECK_EMAIL_AVAILABILITY);
    const [name, setName] = useState('');

    const validateCatalogName = (catalogName) => {
        if (catalogName.includes(' ')) {
            return 'Catalog name cannot contain spaces';
        }
        if (catalogName.length < 3 || catalogName.length > 32) {
            return 'Catalog name must be between 3 and 32 characters';
        }
        if (!/^[a-zA-Z0-9_-]+$/.test(catalogName)) {
            return 'Catalog name can only contain letters, numbers, underscores, and hyphens';
        }
        return null; // No error
    };

    // Only check server availability after client validation passes
    const checkServerAvailability = useCallback(
        debounce(async (catalogName) => {
            setIsCheckingAvailability(true);
            try {
                const { data, error } = await checkCatalogNameAvailability({
                    variables: { catalogName: catalogName.toLowerCase() },
                    errorPolicy: 'all'
                });

                if (error) {
                    console.error('GraphQL error:', error);
                    setIsCatalogNameAvailable(false);
                    setCatalogNameError('Error checking availability. Please try again.');
                    return;
                }

                if (data && typeof data.checkCatalogNameAvailability === 'boolean') {
                    setIsCatalogNameAvailable(data.checkCatalogNameAvailability);
                    if (!data.checkCatalogNameAvailability) {
                        const suggestions = generateSuggestions(catalogName);
                        setSuggestedCatalogNames(suggestions);
                        setCatalogNameError('This catalog name is already taken');
                    } else {
                        setSuggestedCatalogNames([]);
                        setCatalogNameError('');
                    }
                } else {
                    setIsCatalogNameAvailable(false);
                    setCatalogNameError('Error checking availability');
                }
            } catch (error) {
                console.error('Error checking catalog name availability:', error);
                setIsCatalogNameAvailable(false);
                setCatalogNameError('Network error. Please try again.');
            } finally {
                setIsCheckingAvailability(false);
            }
        }, 1000), // Increased to 1 second to reduce calls
        [checkCatalogNameAvailability]
    );

    const handleCatalogNameChange = (e) => {
        const newCatalogName = e.target.value.toLowerCase().replace(/\s+/g, '');
        setCatalogName(newCatalogName);

        // Reset states
        setIsCatalogNameAvailable(null);
        setSuggestedCatalogNames([]);

        // Client-side validation first (no server call)
        const clientValidation = validateSubdomainClient(newCatalogName);

        if (!clientValidation.isValid) {
            setCatalogNameError(clientValidation.error);
            if (clientValidation.error.includes('reserved')) {
                const suggestions = generateSuggestions(newCatalogName);
                setSuggestedCatalogNames(suggestions);
            }
            return;
        }

        // Only make server call if client validation passes
        setCatalogNameError('');
        checkServerAvailability(newCatalogName);
    };

    const handleSuggestionClick = (suggestedName) => {
        setCatalogName(suggestedName);
        setCatalogNameError('');
        setSuggestedCatalogNames([]);
        setIsCatalogNameAvailable(null);

        // Check availability of the suggested name
        const clientValidation = validateSubdomainClient(suggestedName);
        if (clientValidation.isValid) {
            checkServerAvailability(suggestedName);
        }
    };

    const handleFirstStepSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');

        // Password validation
        if (password.length < 6 || (!/^(?=.*[A-Z])/.test(password)) || (!/^(?=.*[a-z])/.test(password))) {
            setErrorMessage('Your new password must be at least 6 characters long and contain both uppercase and lowercase letters.');
            return;
        }

        try {
            const { data } = await checkEmailAvailability({
                variables: { email }
            });

            if (!data.checkEmailAvailability) {
                setErrorMessage('This email is already registered. Please use a different email or login to your account.');
                return;
            }

            setStep(2);
        } catch (error) {
            console.error('Error checking email:', error);
            setErrorMessage('An error occurred. Please try again.');
        }
    };

    const handleFinalSubmit = async (e) => {
        e.preventDefault();

        // Final validation before submission
        const clientValidation = validateSubdomainClient(catalogName);
        if (!clientValidation.isValid) {
            setErrorMessage(clientValidation.error);
            return;
        }

        if (isCatalogNameAvailable === false) {
            setErrorMessage('Please choose an available catalog name');
            return;
        }

        try {
            const { data } = await registerUser({
                variables: {
                    name,
                    email,
                    password,
                    businessName,
                    catalogName: catalogName.toLowerCase(),
                    isSubscribed
                },
                errorPolicy: 'all'
            });

            if (data.register) {
                const { accessToken, refreshToken, user, catalog } = data.register;
                localStorage.setItem('accessToken', accessToken);
                localStorage.setItem('refreshToken', refreshToken);
                login({ ...user, selectedCatalog: catalog });
                navigate('/overview');
            }
        } catch (error) {
            console.error('Registration error:', error);
            if (error.message.includes('email is already registered')) {
                setErrorMessage('This email is already registered. Please use a different email or login to your account.');
                setStep(1);
            } else if (error.message.includes('reserved') || error.message.includes('already in use')) {
                setErrorMessage(error.message);
                // Parse suggestions from server error if available
                const suggestionMatch = error.message.match(/Try these alternatives: (.+)/);
                if (suggestionMatch) {
                    const suggestions = suggestionMatch[1].split(', ');
                    setSuggestedCatalogNames(suggestions);
                } else {
                    setSuggestedCatalogNames(generateSuggestions(catalogName));
                }
            } else {
                setErrorMessage(error.message || 'An error occurred during registration');
            }
        }
    };

    const handleBack = () => {
        setStep(1);
        setErrorMessage('');
    };

    // Determine validation icon
    const getValidationIcon = () => {
        if (isCheckingAvailability) {
            return (
                <svg className="animate-spin h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            );
        }

        if (isCatalogNameAvailable === true) {
            return (
                <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
            );
        }

        if (isCatalogNameAvailable === false) {
            return (
                <svg className="h-4 w-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
            );
        }

        return null;
    };

    return (
        <AuthLayout
            title={step === 1 ? "Create an Account" : "Choose Your Store Link"}
            description={step === 1 ? "Join us today and get started" : "This will be the web address of your catalog"}
        >
            {step === 1 ? (
                <form onSubmit={handleFirstStepSubmit}>
                    <div className="mb-4">
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                        <input
                            type="text"
                            id="name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Your Name"
                            className="w-full px-3 py-2 border bg-white text-black border-gray-300 rounded-md focus:outline-none focus:ring-[#2424db] focus:border-[#2424db]"
                            required
                        />
                    </div>
                    <div className="mb-4">
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="example@domain.com"
                            className="w-full px-3 py-2 border border-gray-300 bg-white text-black rounded-md focus:outline-none focus:ring-[#2424db] focus:border-[#2424db]"
                            required
                        />
                    </div>
                    <div className="mb-4 relative">
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="*************"
                                className="w-full px-3 py-2 pr-10 border border-gray-300 bg-white text-black rounded-md focus:outline-none focus:ring-[#2424db] focus:border-[#2424db]"
                                required
                            />
                            <button
                                type="button"
                                className="absolute top-1 right-2 flex items-center px-3 py-2 bg-gray-50"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-gray-500">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-gray-500">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>
                    <div className="mb-4">
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="newsletter"
                                checked={isSubscribed}
                                onChange={(e) => setIsSubscribed(e.target.checked)}
                                className="h-4 w-4 text-[#191970] focus:ring-[#2424db] border-gray-300 rounded"
                            />
                            <label htmlFor="newsletter" className="ml-2 block text-sm text-gray-700">
                                Subscribe to receive updates and promotional content
                            </label>
                        </div>
                    </div>
                    {errorMessage && <p className="my-4 text-red-500 text-sm text-center">{errorMessage}</p>}

                    <button type="submit" className="w-full bg-[#191970] text-white py-2 px-4 rounded-md hover:bg-opacity-90 transition duration-300">Continue</button>
                </form>
            ) : (
                <form onSubmit={handleFinalSubmit}>
                    <div className="mb-4">
                        <button
                            type="button"
                            onClick={handleBack}
                            className="text-[#191970] hover:underline bg-gray-50 flex items-center"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Back
                        </button>
                    </div>

                    <div className="mb-6">
                        <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-1">Store Name</label>
                        <input
                            type="text"
                            id="businessName"
                            value={businessName}
                            onChange={(e) => setBusinessName(e.target.value)}
                            placeholder="Mary's Bakery"
                            className="w-full px-3 py-2 mb-5 border bg-white text-black border-gray-300 rounded-md focus:outline-none focus:ring-[#2424db] focus:border-[#2424db]"
                            required
                        />
                        <label htmlFor="catalogName" className="block text-sm font-medium text-gray-700 mb-1">Store Link</label>
                        <div className="mt-1">
                            <div className="relative bg-white border border-gray-300 rounded-lg  focus-within:border-[#2424db] transition-all duration-200">
                                <div className="flex items-center p-3">
                                    <span className="text-gray-500 text-base font-medium whitespace-nowrap">
                                        https://
                                    </span>
                                    <input
                                        type="text"
                                        id="catalogName"
                                        className="flex-1 mx-1 px-2 py-1 text-base text-[#191970] bg-yellow-50 border border-yellow-200 rounded focus:outline-none focus:bg-white focus:border-[#2424db] transition-all duration-200"
                                        placeholder="marysbakery"
                                        value={catalogName}
                                        onChange={handleCatalogNameChange}
                                    />
                                    <span className="text-gray-500 text-base font-medium whitespace-nowrap">
                                        .pricilog.com
                                    </span>
                                    {(isCheckingAvailability || isCatalogNameAvailable !== null) && (
                                        <span className="ml-3 flex items-center">
                                            {getValidationIcon()}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Live Preview */}
                            {catalogName && !catalogNameError && (
                                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                                    <p className="text-sm text-green-700">
                                        ✓ Your store will be live at:
                                        <span className="font-semibold ml-1">https://{catalogName}.pricilog.com</span>
                                    </p>
                                </div>
                            )}
                        </div>
                        {catalogNameError && <p className="mt-2 text-sm text-red-600">{catalogNameError}</p>}

                        <p className="text-sm text-gray-500 mt-2">
                            Choose a unique link for your store. Use only letters, numbers, and hyphens (3-32 characters).
                        </p>
                        {/* {!catalogNameError && catalogName && (
                            <p className="mt-2 text-xs text-gray-500">
                                Your catalog will be accessible at{' '}
                                <span className='text-[#191970] font-medium'>
                                    https://{catalogName}.pricilog.com
                                </span>
                            </p>
                        )} */}
                    </div>

                    {suggestedCatalogNames.length > 0 && (
                        <div className="mb-6 p-4 bg-blue-50 rounded-md">
                            <p className="text-sm font-medium text-gray-700 mb-2">
                                Try these alternatives:
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {suggestedCatalogNames.map((suggestedName, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className="px-3 py-1 text-sm bg-white border border-[#191970] text-[#191970] rounded-md hover:bg-[#191970] hover:text-white transition-colors"
                                        onClick={() => handleSuggestionClick(suggestedName)}
                                    >
                                        {suggestedName}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {errorMessage && <p className="my-4 text-red-500 text-sm text-center">{errorMessage}</p>}

                    <button
                        type="submit"
                        className="w-full bg-[#191970] text-white py-2 px-4 rounded-md hover:bg-opacity-90 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!isCatalogNameAvailable || !!catalogNameError || loading || isCheckingAvailability}
                    >
                        {loading ? 'Creating Your Store...' : 'Complete Registration'}
                    </button>
                </form>
            )}

            {step === 1 && (
                <p className="mt-8 text-center text-sm text-gray-600">
                    Already have an account?{' '}
                    <Link to="/login" className="font-medium text-[#191970] hover:underline">Login</Link>
                </p>
            )}
        </AuthLayout>
    );
};

export default Register;